import { Injectable } from '@angular/core';
import { AppointmentService } from './appointment.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Patient } from '../models/patient.model';
import { PatientService } from './patient.service';
import { AppPaths } from '../enums/app-paths.enum';
import { NavigationService } from './navigation.service';

@Injectable({
  providedIn: 'root'
})
export class AppointmentStepperService {
  set currentStepIndex(index: number) {
    sessionStorage.setItem('appointmentCurrentStepIndex', index.toString());
  }

  get currentStepIndex(): number {
    return +(sessionStorage.getItem('appointmentCurrentStepIndex') || 0);
  }

  set steps(steps: number) {
    sessionStorage.setItem('appointmentSteps', steps.toString());
  }

  get steps(): number {
    return +(sessionStorage.getItem('appointmentSteps') || 0);
  }

  set historySteps(historyStep: string) {
    const historySteps = new Set(this.historySteps || []);
    historySteps.add(historyStep);
    sessionStorage.setItem('appointmentHistorySteps', JSON.stringify(Array.from(historySteps)));
  }

  get historySteps(): string[] | null {
    let historySteps = sessionStorage.getItem('appointmentHistorySteps');
    return historySteps ? (JSON.parse(historySteps) as string[]) : null;
  }

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    private appointmentService: AppointmentService,
    private patientService: PatientService,
    private router: Router,
    private navigationService: NavigationService
  ) {}

  getMaxSteps(): number {
    if (this.steps) return this.steps;

    let steps = 5;
    if (this.appointmentService.skipSelectSpecialty) steps--;
    if (this.appointmentService.skipSelectCaller) steps--;
    this.steps = steps;
    return steps;
  }

  private setStepIndexParam(): void {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('index', this.currentStepIndex.toString());
    this.location.replaceState(`${window.location.pathname}?${queryParams.toString()}`);
  }

  private getStepIndexFromUrl(): void {
    const step = +this.route.snapshot.queryParams['index'];
    if (step >= 0) this.currentStepIndex = step;
    else this.setStepIndexParam();
  }

  getCurrentStepIndex(): number {
    this.getStepIndexFromUrl();
    return this.currentStepIndex;
  }

  incrementStepIndex(): void {
    this.currentStepIndex++;
  }

  decrementStepIndex(): void {
    this.currentStepIndex--;
  }

  reset(): void {
    sessionStorage.removeItem('appointmentCurrentStepIndex');
    sessionStorage.removeItem('appointmentSteps');
    sessionStorage.removeItem('appointmentHistorySteps');
    sessionStorage.removeItem('firstAppointmentShortFlowView');
  }

  async goToNextStepAppointmentShortFlow(me?: Patient): Promise<void> {
    me = me || (await this.patientService.getMe());
    const hasFamilyGroup = await this.patientService.hasFamilyGroup(true);

    const navigateToNextStep = () => {
      this.reset();

      if (!this.appointmentService.patient && !this.appointmentService.specialty) {
        this.router.navigate([AppPaths.NAVBAR, AppPaths.APPOINTMENTS, AppPaths.MY_APPOINTMENTS]);
      } else if (!this.appointmentService.patient && hasFamilyGroup) {
        this.markFirstScheduleAppointmentFlowView(`/${AppPaths.NAVBAR}/${AppPaths.APPOINTMENTS}/${AppPaths.SELECT_PATIENT}`);
        this.router.navigate([AppPaths.NAVBAR, AppPaths.APPOINTMENTS, AppPaths.SELECT_PATIENT]);
      } else if (!this.appointmentService.specialty) {
        this.markFirstScheduleAppointmentFlowView(`/${AppPaths.NAVBAR}/${AppPaths.APPOINTMENTS}/${AppPaths.SELECT_SPECIALTY}`);
        this.router.navigate([AppPaths.NAVBAR, AppPaths.APPOINTMENTS, AppPaths.SELECT_SPECIALTY]);
      } else {
        this.markFirstScheduleAppointmentFlowView(`/${AppPaths.NAVBAR}/${AppPaths.APPOINTMENTS}/${AppPaths.SELECT_DOCTOR}`);
        this.router.navigate([AppPaths.NAVBAR, AppPaths.APPOINTMENTS, AppPaths.SELECT_DOCTOR]);
      }
    };

    if (me.provider.useTermsAndConditions) {
      await this.patientService.checkTermsAndConditionsAccepted(navigateToNextStep);
    } else {
      navigateToNextStep();
    }
  }

  setupBackButtonForScheduleAppointmentFlow(): void {
    if (this.isFirstAppointmentShortFlowView()) {
      this.navigationService.hideBackButton();
    } else {
      this.navigationService.showBackButton();
    }
  }

  private markFirstScheduleAppointmentFlowView(path?: string): void {
    const appointmentShortFlow = this.appointmentService.isAppointmentShortFlow();
    if (appointmentShortFlow && !this.firstAppointmentShortFlowView()) {
      const routeToMark = path || window.location.pathname;
      sessionStorage.setItem('firstAppointmentShortFlowView', routeToMark);
    }
  }

  private isFirstAppointmentShortFlowView(): boolean {
    const appointmentShortFlow = this.appointmentService.isAppointmentShortFlow();
    if (!appointmentShortFlow) return false;
    return this.firstAppointmentShortFlowView() === window.location.pathname;
  }

  private firstAppointmentShortFlowView(): string | null {
    return sessionStorage.getItem('firstAppointmentShortFlowView');
  }
}
